/**
 * Keeps CSS custom properties in sync with the Visual Viewport so fixed chrome
 * (tab bar, docks, chat composer) tracks the soft keyboard.
 *
 * Same math everywhere (web + Telegram): the OS keyboard is exposed via
 * visualViewport, not Telegram.WebApp.viewportHeight (that API tracks Mini App
 * expand/collapse, not the keyboard — using it made TG feel “broken” vs web).
 *
 * Telegram-only anti-jank: ignore visualViewport.scroll (extremely noisy in TG
 * WebViews) and skip redundant style writes when values are unchanged.
 */

const ROOT_VARS = {
  height: '--cs-vv-height',
  offsetTop: '--cs-vv-offset-top',
  offsetBottom: '--cs-vv-offset-bottom',
} as const;

let raf = 0;
let lastHeight = '';
let lastOffsetTop = '';
let lastOffsetBottom = '';
let telegramViewportBound = false;

function isTelegramMode(): boolean {
  if (typeof document === 'undefined') return false;
  if (document.documentElement.dataset.appMode === 'telegram') return true;
  return Boolean(window.Telegram?.WebApp?.initData);
}

function setVarIfChanged(root: HTMLElement, name: string, value: string, prev: string): string {
  if (prev === value) return prev;
  root.style.setProperty(name, value);
  return value;
}

function writeViewportVars(): void {
  const root = document.documentElement;
  const vv = window.visualViewport;

  if (!vv) {
    lastHeight = setVarIfChanged(root, ROOT_VARS.height, `${window.innerHeight}px`, lastHeight);
    lastOffsetTop = setVarIfChanged(root, ROOT_VARS.offsetTop, '0px', lastOffsetTop);
    lastOffsetBottom = setVarIfChanged(root, ROOT_VARS.offsetBottom, '0px', lastOffsetBottom);
    return;
  }

  // Same formula as plain mobile web — keyboard = layout bottom − visual bottom.
  const offsetBottom = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
  const height = Math.round(vv.height);
  const offsetTop = Math.round(vv.offsetTop);

  lastHeight = setVarIfChanged(root, ROOT_VARS.height, `${height}px`, lastHeight);
  lastOffsetTop = setVarIfChanged(root, ROOT_VARS.offsetTop, `${offsetTop}px`, lastOffsetTop);
  lastOffsetBottom = setVarIfChanged(root, ROOT_VARS.offsetBottom, `${offsetBottom}px`, lastOffsetBottom);
}

/** Sync CSS vars now. Safe to call from Telegram viewport handlers. */
export function syncVisualViewport(): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  writeViewportVars();
}

function scheduleViewportVars(): void {
  if (raf) return;
  raf = window.requestAnimationFrame(() => {
    raf = 0;
    writeViewportVars();
  });
}

function bindTelegramViewport(): void {
  if (telegramViewportBound || typeof window === 'undefined') return;
  const tg = window.Telegram?.WebApp;
  if (!tg || typeof tg.onEvent !== 'function') return;

  telegramViewportBound = true;
  // Mini App chrome resize (expand/fullscreen) — still resync VV after it.
  tg.onEvent('viewportChanged', scheduleViewportVars);
  scheduleViewportVars();
}

/** Call once at app boot. Safe to call multiple times. */
export function initVisualViewport(): void {
  if (typeof window === 'undefined') return;

  writeViewportVars();
  bindTelegramViewport();

  const vv = window.visualViewport;
  if (vv) {
    vv.addEventListener('resize', scheduleViewportVars);
    // visualViewport.scroll is the main keyboard jank source inside Telegram
    // WebViews. Plain Safari still needs it for URL-bar / pan tracking.
    const onScroll = () => {
      if (isTelegramMode()) return;
      scheduleViewportVars();
    };
    vv.addEventListener('scroll', onScroll);
  }

  window.addEventListener('resize', scheduleViewportVars);
  window.addEventListener('orientationchange', scheduleViewportVars);
}

/** Hook Telegram viewport events after WebApp.ready / expand (idempotent). */
export function bindTelegramViewportSync(): void {
  bindTelegramViewport();
  syncVisualViewport();
}
